import Link from 'next/link'
import * as R from 'ramda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Waypoint } from 'react-waypoint'
import styled, { css } from 'styled-components'

import BackToTop from '../../components/BackToTop'
import NextSeo from '../../components/NextSeo'
import StickyBox from '../../components/StickyBox'
import { AppviewNavigation } from '../../containers/Appview'
import ArticleItem from '../../containers/Article/ArticleItem'
import Button from '../../containers/Button'
import { useTracking } from '../../hooks'
import { useTranslation } from '../../i18n'
import {
  getArticleListByTagIdRequest,
  getArticleListByTagIdsRequest
} from '../../redux/article/actions'
import { getArticleIds } from '../../redux/article/selectors'
import { clearRedux } from '../../redux/master/actions'
import { getColor, getFontSize } from '../../utils/cssUtils'
import {
  articleChangeTab,
  articleListingClickArticle,
  articleListingClickMoreArticle,
  articleListingView
} from '../../utils/dataTracker'
import mq from '../../utils/mediaQuery'

const Wrapper = styled.div`
  width: 100%;
  margin: 0 auto;
  background-color: #fff;
  display: flex;
  flex-flow: column nowrap;
`
const ArticleTitle = styled.h1`
  width: 792px;
  font-size: ${getFontSize('headline')};
  color: ${getColor('black')};
  text-align: left;
  margin: 27px auto 24px auto;
  ${mq.tablet} {
    width: 640px;
  }
  ${mq.mobile} {
    width: 100%;
    margin-top: 24px;
    padding: 0 12px;
  }
`
const StyledStickyBox = styled(StickyBox)`
  background-color: ${getColor('white')};
  z-index: 1;
  transition: box-shadow 0.3s ease-in-out;
  ${({ isScrolled }) =>
    isScrolled &&
    css`
      box-shadow: 0 2px 20px 0 rgba(0, 0, 0, 0.2);
    `}
`
const Tags = styled.div`
  display: flex;
  flex-flow: row wrap;
  margin: 0 auto;
  max-width: 792px;
  width: 100%;
  ${mq.tablet} {
    max-width: 640px;
  }
  ${mq.mobile} {
    padding: 0 12px;
  }
`
const TagsContainer = styled.div`
  margin: 0 -12px;
`
const TagTitle = styled.span`
  display: inline-block;
  margin: 0 12px 24px 12px;
  font-size: ${getFontSize('body2')};
`
const TagTitleLink = styled.a`
  display: inline-block;
  ${({ selected }) =>
    selected &&
    css`
      color: ${getColor('theme')};
      position: relative;
      &::after {
        content: '';
        display: block;
        width: 100%;
        height: 2px;
        background-color: ${getColor('theme')};
        position: absolute;
        bottom: -2px;
      }
    `}
`
const Articles = styled.div`
  flex-grow: 1;
  padding: 0 12px;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
`
const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 63px 0 60px;
  ${mq.tablet} {
    margin-bottom: 33px;
  }
  ${mq.mobile} {
    margin-bottom: 58px;
  }
`
const MoreBtn = styled(props => <Button fullWidth height={48} {...props} />)`
  max-width: 180px;
`

const tagIds = [
  process.env.ARTICLE_TAG_ID_KIND_FRIDAY,
  process.env.ARTICLE_TAG_ID_FEATURED_PROJECTS,
  process.env.ARTICLE_TAG_ID_CHARITY_EVENTS,
  process.env.ARTICLE_TAG_ID_URGENT_PROJECTS,
  process.env.ARTICLE_TAG_ID_CORPORATE
]
const tagSlugs = [
  'kindFriday',
  'featuredProjects',
  'charityEvents',
  'urgentProjects',
  'corporate'
]

const Article = ({ selectedTagId, limit }) => {
  const [isScrolled, setIsScrolled] = useState(false)
  const { t } = useTranslation('common')
  const dispatch = useDispatch()
  const track = useTracking()
  const isAppview = useSelector(R.path(['appState', 'isAppview']))
  const headerHeight = useMemo(() => (isAppview ? 44 : 64), [isAppview])
  const articleIds = useSelector(getArticleIds) || []
  const [hasMoreArticle, nextOffset, isFetching] = useSelector(
    R.paths([
      ['articleState', 'hasMoreArticle'],
      ['articleState', 'nextOffset'],
      ['articleState', 'fetching', 'getArticlePage']
    ])
  )
  const tags = useMemo(
    () =>
      R.pipe(
        R.zipWith((tagId, tagTitle) => ({ tagId, tagTitle }), tagIds),
        R.addIndex(R.map)((tagItem, i) => ({ ...tagItem, slug: tagSlugs[i] }))
      )([
        t('article.kindFriday'),
        t('article.featuredProjects'),
        t('article.charityEvents'),
        t('article.urgentProjects'),
        t('article.corporate')
      ]),
    []
  )

  const selectedTagTitle = R.isNil(selectedTagId)
    ? t('article.allArticles')
    : R.prop('tagTitle')(R.find(R.propEq('tagId', selectedTagId))(tags))

  const loadMoreArticle = useCallback(() => {
    track(articleListingClickMoreArticle(selectedTagTitle))
    if (!R.isNil(selectedTagId)) {
      dispatch(getArticleListByTagIdRequest(selectedTagId, nextOffset, limit))
    } else {
      dispatch(getArticleListByTagIdsRequest(tagIds, nextOffset, limit))
    }
  }, [nextOffset, limit])

  const handleArticleClick = articleId => () => {
    track(articleListingClickArticle(articleId, selectedTagTitle))
  }

  const handleTagClick = newTagTitle => e => {
    track(articleChangeTab(newTagTitle, selectedTagTitle))
  }

  const handleScroll = useCallback(
    ({ waypointTop }) => {
      setIsScrolled(waypointTop !== headerHeight)
    },
    [headerHeight]
  )

  useEffect(() => {
    track(articleListingView(selectedTagTitle), true)
    return () => {
      dispatch(clearRedux(['articleState', 'result']))
    }
  }, [])

  return (
    <>
      <Wrapper>
        <Waypoint onPositionChange={handleScroll} topOffset={headerHeight} />
        <NextSeo
          title={t('seo.corporate.article.ogTitle')}
          description={t('seo.corporate.article.ogDescription')}
        />
        <StyledStickyBox offsetTop={headerHeight} isScrolled={isScrolled}>
          <ArticleTitle>{t('article.pageTitle')}</ArticleTitle>
          <Tags>
            <TagsContainer>
              {
                <TagTitle key='allArticles'>
                  <Link href='/article'>
                    <TagTitleLink
                      selected={R.isNil(selectedTagId)}
                      onClick={handleTagClick(t('article.allArticles'))}
                    >
                      {t('article.allArticles')}
                    </TagTitleLink>
                  </Link>
                </TagTitle>
              }
              {R.map(
                ({ tagId, tagTitle, slug }) => (
                  <TagTitle key={tagId}>
                    <Link
                      href={`/article?tagId=${tagId}`}
                      as={`/article?tag=${slug}`}
                      passHref
                    >
                      <TagTitleLink
                        selected={tagId === selectedTagId}
                        onClick={handleTagClick(tagTitle)}
                      >
                        {tagTitle}
                      </TagTitleLink>
                    </Link>
                  </TagTitle>
                ),
                tags
              )}
            </TagsContainer>
          </Tags>
        </StyledStickyBox>
        <Articles>
          {R.isEmpty(articleIds) ? (
            isFetching ? (
              R.times(index => <ArticleItem key={index} />, 8)
            ) : (
              <div>{t('article.articleNoMore')}</div>
            )
          ) : (
            articleIds.map(articleId => (
              <ArticleItem
                articleId={articleId}
                key={articleId}
                onClick={handleArticleClick(articleId)}
              />
            ))
          )}
        </Articles>
        <PaginationWrapper>
          {hasMoreArticle ? (
            <MoreBtn onClick={loadMoreArticle} loading={isFetching}>
              {t('article.articleMore')}
            </MoreBtn>
          ) : (
            !R.isEmpty(articleIds) && <div>{t('article.articleNoMore')}</div>
          )}
        </PaginationWrapper>
      </Wrapper>
      <BackToTop />
    </>
  )
}

Article.getInitialProps = async ({ query, store }) => {
  const offset = 0
  const limit = 8
  const { tagId } = query
  if (!R.isNil(tagId)) {
    store.dispatch(getArticleListByTagIdRequest(tagId, offset, limit))
  } else {
    store.dispatch(getArticleListByTagIdsRequest(tagIds, offset, limit))
  }
  return {
    limit,
    selectedTagId: tagId
  }
}

Article.renderPersistentLayout = () => ({
  appviewNavigation: () => <AppviewNavigation />
})

export default Article
